import React, { useState, useEffect } from 'react';

const ConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  confirmText = "Delete", 
  cancelText = "Cancel",
  danger = true,
  requireCaptcha = false,
  captchaText = null
}) => {
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaError, setCaptchaError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentCaptcha, setCurrentCaptcha] = useState('');

  // Generate random captcha
  const generateCaptcha = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  useEffect(() => {
    if (isOpen) {
      setCaptchaInput('');
      setCaptchaError('');
      setIsSubmitting(false);
      // Generate new captcha when modal opens
      if (requireCaptcha) {
        setCurrentCaptcha(generateCaptcha());
      }
    }
  }, [isOpen, requireCaptcha]);

  const handleConfirm = async () => {
    if (requireCaptcha) {
      if (captchaInput !== currentCaptcha) {
        setCaptchaError(`Please type "${currentCaptcha}" to confirm`);
        return;
      }
      setCaptchaError('');
    }

    setIsSubmitting(true);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      console.error('Confirmation error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl shadow-2xl border border-gray-700 max-w-md w-full">
        {/* Header */}
        <div className="p-6 border-b border-gray-700">
          <h3 className={`text-xl font-semibold ${danger ? 'text-red-400' : 'text-white'}`}>
            {title}
          </h3>
        </div>

        {/* Body */}
        <div className="p-6">
          <p className="text-gray-300 mb-6">
            {message}
          </p>

          {requireCaptcha && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Type <span className="font-mono text-red-400 font-bold">{currentCaptcha}</span> to confirm:
              </label>
              <input
                type="text"
                value={captchaInput}
                onChange={(e) => setCaptchaInput(e.target.value)}
                className={`w-full px-4 py-3 bg-gray-700 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-white placeholder-gray-400 transition-all ${
                  captchaError ? 'border-red-500' : 'border-gray-600'
                }`}
                placeholder={`Type "${currentCaptcha}"`}
                autoComplete="off"
              />
              {captchaError && (
                <p className="text-red-400 text-sm mt-2">{captchaError}</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-700 flex space-x-3">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 bg-gray-600 text-white px-4 py-3 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            disabled={isSubmitting || (requireCaptcha && captchaInput !== currentCaptcha)}
            className={`flex-1 px-4 py-3 rounded-lg transition-colors disabled:opacity-50 ${
              danger 
                ? 'bg-red-600 text-white hover:bg-red-700' 
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isSubmitting ? 'Processing...' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal; 